import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";
import { useRoute } from "vue-router";
import { useRouteQuery } from "../api/route-query.ts";
import { FAMILY_DISPLAY } from "../lib/catalog-config.ts";
import { can, type Session } from "../session/access.ts";
import { useProducts } from "./customer.ts";

/*
 * What the sidebar shows beyond the routes (start version of the design prototype, AP-F10.1): the
 * counters at the staff's orders and work lists (C-08), and which product family's item stays
 * current on a product's page (ADR-150 point 4), read from routes the session may call anyway;
 * nothing here decides a permission or computes a value the API does not give. The way to a
 * product's tools left the sidebar (ADR-150 point 2).
 */

/** A counter at a navigation item; `label` is what a screen reader hears after the title. */
export type NavCount = { count: number; label: string };

/**
 * Received orders, open tasks and new inquiries over all customers (C-08, the orders by the TL
 * 2026-10-23), from `total` of the three lists with one row each. A list the session may not read
 * is not asked for; changes refresh them with the lists (src/api/invalidation.ts).
 */
export function useNavCounts(session: MaybeRefOrGetter<Session>) {
  // customers list `order.view` too, but read their own orders, not the staff's list
  const mayOrders = () => Boolean(toValue(session)?.staffRole) && can(toValue(session), "order.view");
  const mayTasks = () => can(toValue(session), "task.complete");
  const mayInquiries = () => can(toValue(session), "inquiry.process");
  const orders = useRouteQuery("staffOrderList", { query: { status: "received", size: 1 } }, { enabled: mayOrders });
  const tasks = useRouteQuery("staffTaskList", { query: { size: 1 } }, { enabled: mayTasks });
  const inquiries = useRouteQuery("staffInquiryList", { query: { status: "new", size: 1 } }, { enabled: mayInquiries });
  return computed(() => {
    const counts: Partial<Record<string, NavCount>> = {};
    const received = mayOrders() ? (orders.data.value?.total ?? 0) : 0;
    const open = mayTasks() ? (tasks.data.value?.total ?? 0) : 0;
    const fresh = mayInquiries() ? (inquiries.data.value?.total ?? 0) : 0;
    if (received > 0) counts["staffOrders"] = { count: received, label: `${received} neu` };
    if (open > 0) counts["staffTasks"] = { count: open, label: `${open} offen` };
    if (fresh > 0) counts["staffInquiries"] = { count: fresh, label: `${fresh} neu` };
    return counts;
  });
}

/**
 * The menu item that stays current on a product's page, as the prototype keeps "feratel Agenten"
 * current on an agent's page (`navItem`): the route's `meta.navItem` (an agent's detail page under
 * `/catalog/feratel-agents/<slug>`, ADR-150 addendum of 2026-10-29); on the generic product page
 * `/catalog/:slug` its family's page, by the family's concept id (ADR-112). `null` elsewhere and for
 * a product without a family page. Reads the catalogue only on the generic product page, which
 * reads it anyway.
 */
export function useProductFamilyItem(): ComputedRef<string | null> {
  const route = useRoute();
  const onProduct = () => route.name === "product";
  const products = useProducts({ enabled: onProduct });
  return computed(() => {
    if (route.meta.navItem) return route.meta.navItem;
    if (!onProduct()) return null;
    const product = products.data.value?.items.find((candidate) => candidate.slug === route.params.slug);
    return product?.family ? (FAMILY_DISPLAY.get(product.family.id)?.page?.route ?? null) : null;
  });
}
